import AccordianBanner from 'components/UI/AccordianBanner';
import RichText from 'components/UI/Richtext';
import Link from 'components/UI/Link';
import { ReactElement, useState } from 'react';
import { Document } from '@contentful/rich-text-types';

export type AccordionDoctor = {
  slug: string;
  name: string;
};

export interface DoctorHubsAccordionProps {
  id: string;
  heading: Document;
  copy?: Document | undefined;
  copyAlignment?: 'left' | 'center' | 'right';
  doctorHubsGroupings?: Array<{
    fields: {
      id: string;
      heading: Document;
      copy?: Document | undefined;
      doctors: AccordionDoctor[];
    };
  }>;
}

export default function DoctorHubsAccordion({
  id,
  heading,
  copy,
  copyAlignment = 'center',
  doctorHubsGroupings,
}: DoctorHubsAccordionProps): ReactElement {
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set());
  const hubs = doctorHubsGroupings ?? [];

  const toggleAccordion = (index: number): void => {
    setOpenIndices((prev) => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  const alignmentClass =
    copyAlignment === 'left'
      ? 'text-left'
      : copyAlignment === 'right'
      ? 'text-right'
      : 'text-center';

  return (
    <div id={id} className="py-10 md:py-20 px-16 px-site-component mx-auto bg-neutral-alt">
      <div className="flex flex-col lg:flex-row justify-center gap-8 lg:gap-6">
        {heading && (
          <div className="w-full lg:w-1/2">
            <div className="bordered-heading-richtext text-left text-primary-actual md:mb-8 font-light">
              <RichText
                document={heading}
                classNames="text-new-34 lg:text-4xl lg:leading-new-43 max-w-none richtext-nomargin-headings richtext-font-inherit"
                noEmpty={true}
              />
            </div>
          </div>
        )}
        {copy && (
          <div className="w-full lg:w-1/2">
            <RichText
              document={copy}
              classNames={`text-lg text-primary-actual ${alignmentClass}`}
            />
          </div>
        )}
      </div>
      {(heading || copy) && <div className="block mb-8 lg:mb-16"></div>}
      {hubs.length === 0 ? (
        <p className="text-lg text-primary-actual">No doctors to show</p>
      ) : (
        hubs.map((hub, i) => {
          const hubFields = hub.fields;

          return (
            <div key={hubFields.id || i}>
              <AccordianBanner
                className="bg-white p-4 md:px-8 md:py-6 accordion-location-hub-heading accordion-doctor-hub-heading"
                type="FAQ"
                onClick={() => toggleAccordion(i)}
                isOpen={openIndices.has(i)}
                header={
                  <RichText
                    document={hubFields.heading}
                    classNames="max-w-none font-light"
                    noEmpty={true}
                  />
                }
              />
              {openIndices.has(i) && (
                <div className="p-4 md:pt-6 md:pb-8 md:px-8 bg-white border-t border-gray-300">
                  {hubFields.copy && (
                    <div className="mb-4">
                      <RichText
                        document={hubFields.copy}
                        classNames="text-lg text-primary-actual"
                      />
                    </div>
                  )}
                  {hubFields.doctors && hubFields.doctors.length > 0 && (
                    <ul className="list-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-4 gap-x-6">
                      {hubFields.doctors.map((doctor) => (
                        <li key={doctor.slug} className="h-full">
                          <Link slug={`doctors/${doctor.slug}`}>
                            <a className="text-lg teal-fontcolor hover:text-primary-actual bg-neutral-alt hover:bg-neutral-alt/30 p-2.5 flex items-center justify-start gap-3 h-full">
                              <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M16.6667 17.5V15.8333C16.6667 14.9493 16.3155 14.1014 15.6904 13.4763C15.0652 12.8512 14.2174 12.5 13.3333 12.5H6.66666C5.78261 12.5 4.93476 12.8512 4.30964 13.4763C3.68452 14.1014 3.33333 14.9493 3.33333 15.8333V17.5"
                                    stroke="white"
                                    strokeWidth="1.66667"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M10 9.16667C11.8409 9.16667 13.3333 7.67428 13.3333 5.83333C13.3333 3.99238 11.8409 2.5 10 2.5C8.15905 2.5 6.66666 3.99238 6.66666 5.83333C6.66666 7.67428 8.15905 9.16667 10 9.16667Z"
                                    stroke="white"
                                    strokeWidth="1.66667"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <span className="flex-grow">{doctor.name}</span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="11"
                                height="19"
                                viewBox="0 0 11 19"
                                fill="none"
                              >
                                <path
                                  d="M1.33301 1.33301L9.33301 9.33301L1.33301 17.333"
                                  stroke="#00C1D4"
                                  strokeWidth="2.66667"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </a>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="block mb-4"></div>
            </div>
          );
        })
      )}
    </div>
  );
}
